import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize, ZoomOut, Monitor } from 'lucide-react';
import './TitleSlide.css';

// Automatically import all images from the folder
const importAll = (r) => r.keys().map(r);
const stripImages = importAll(
  require.context('../images/first slide moving strip/', false, /\.(png|jpe?g|svg|webp|gif)$/i)
);

function TitleSlide({ showNotes }) {
  const [showHint, setShowHint] = useState(() => {
    // Prüfe ob der Hint schon geschlossen wurde
    return sessionStorage.getItem('hintDismissed') !== 'true';
  });

  const dismissHint = () => {
    setShowHint(false);
    sessionStorage.setItem('hintDismissed', 'true');
  };

  return (
    <div className="slide title-slide">
      {/* Fullscreen Hint Modal */}
      <AnimatePresence>
        {showHint && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="hint-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="hint-modal"
            >
              {/* Decorative top border */}
              <div className="hint-top-border" />
              
              {/* Animated glow effect */}
              <div className="hint-glow" />
              
              {/* Close button */}
              <button
                onClick={dismissHint}
                className="hint-close-btn"
              >
                <X size={16} />
              </button>

              <div className="hint-content">
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="hint-icon-container"
                >
                  <Monitor size={32} className="hint-icon" />
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="hint-title"
                >
                  Optimale Darstellung
                </motion.h2>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="hint-subtitle"
                >
                  Für das beste Erlebnis empfehlen wir:
                </motion.p>

                {/* Options */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="hint-options"
                >
                  <div className="hint-option">
                    <div className="hint-option-icon hint-option-icon-red">
                      <Maximize size={20} />
                    </div>
                    <div className="hint-option-text">
                      <p className="hint-option-title">Vollbildmodus aktivieren</p>
                      <p className="hint-option-desc">Drücke F oder nutze das Browsermenü</p>
                    </div>
                  </div>

                  <div className="hint-divider-text">
                    — oder —
                  </div>

                  <div className="hint-option">
                    <div className="hint-option-icon hint-option-icon-blue">
                      <ZoomOut size={20} />
                    </div>
                    <div className="hint-option-text">
                      <p className="hint-option-title">Browser-Zoom auf 80%</p>
                      <p className="hint-option-desc">Drücke Strg + Minus (Cmd + Minus auf Mac)</p>
                    </div>
                  </div>
                </motion.div>

                {/* Button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  onClick={dismissHint}
                  className="hint-start-btn"
                >
                  Verstanden, Präsentation starten
                </motion.button>

                {/* Skip text */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="hint-skip-text"
                >
                  Diese Meldung erscheint nur einmal
                </motion.p>
              </div>

              {/* Decorative bottom border */}
              <div className="hint-bottom-border" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animated SVG Border */}
      <svg className="animated-border-svg" width="100%" height="100%" viewBox="0 0 1280 720" preserveAspectRatio="none">
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#dc2626" stopOpacity="0" />
            <stop offset="50%" stopColor="#dc2626" stopOpacity="1" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {/* Static background border */}
        <rect 
          x="20" y="20" 
          width="1240" height="680" 
          rx="12" ry="12"
          className="border-static" 
        />
        
        {/* Moving Line 1 (Red) */}
        <rect 
          x="20" y="20" 
          width="1240" height="680" 
          rx="12" ry="12"
          className="border-moving-1" 
        />
        
        {/* Moving Line 2 (Blue/Mirrored) */}
        <rect 
          x="20" y="20" 
          width="1240" height="680" 
          rx="12" ry="12"
          className="border-moving-2" 
        />
      </svg>

      <div className="title-content">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="title-label"
        >
          Präsentation
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="main-title"
        >
          <span className="title-line-1">Rechtsextremismus</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="title-subtitle"
        >
          Strategien, Akteure und die Gefahr für die Demokratie
        </motion.p>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1, duration: 0.8, ease: "easeOut" }}
          className="title-divider"
        />

        {/* Author Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="author-info"
        >
          <div className="author-field">
            <span className="field-label">Namen</span>
            <span className="field-value">
              Majed Asaad<br/>
              Yekcan Sevim<br/>
              Samir Mohammed
            </span>
          </div>
          <div className="author-field">
            <span className="field-label">Klasse</span>
            <span className="field-value">12FO21</span>
          </div>
          <div className="author-field">
            <span className="field-label">Datum</span>
            <span className="field-value">09.01.2026</span>
          </div>
        </motion.div>

        {/* Image Strip Area */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="title-image-strip-container"
        >
          <div className="image-strip-track">
            {/* Duplicate images for seamless loop */}
            {[...stripImages, ...stripImages].map((img, index) => (
              <div key={index} className="strip-image-item">
                <img src={img} alt={`Slide visual ${index}`} />
              </div>
            ))}
          </div>
          <div className="strip-overlay"></div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="scroll-hint"
      >
        <span>Drücke → um zu starten</span>
        <motion.div
          animate={{ x: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="arrow"
        >
          →
        </motion.div>
      </motion.div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Einstieg (The Cold Open):</strong> Die Präsentation startet ohne Begrüßung mit einer Analyse: 
          Rechtsextreme Content-Creator nutzen reale Probleme (Drogen, Verwahrlosung) und rahmen sie ethnisch ("unsere Ingenieure"). 
          Die Botschaft: "Das System hat versagt".<br/><br/>
          <strong>Die These:</strong> Diese Akteure (YouTuber, Influencer) sind keine Spinner am Rand, sondern Teil einer Strategie. 
          Sie bauen keine Partei, sondern eine "Gegenkultur". Wir zeigen heute, wie sie die Demokratie "hacken".
        </div>
      )}
    </div>
  );
}

export default TitleSlide;